import React from 'react';
import type { RunSummary } from '@root/lib';
import { TABLE_MIN_WIDTH, useWideEnough } from '@shared/history';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import ActiveRoundNote from './ActiveRoundNote';
import RunCard from './RunCard';
import styles from './RunList.module.scss';
import RunRow from './RunRow';

export type RunTab = 'completed' | 'live';

interface RunHistoryPanelProps {
  runs: RunSummary[];
  symbol: string;
  tab: RunTab;
  onTabChange: (tab: RunTab) => void;
  onView: (run: RunSummary) => void;
  canLoadMore: boolean;
  onLoadMore: () => void;
  // The live run's countdown, when one is going.
  primarySkin: string;
  secondarySkin: string;
  periodLabel: string;
  resultLabel: string;
  onOpenFilters: () => void;
}

const COLUMNS: [string, string][] = [
  ['Round', 'cell_round'],
  ['Timestamp', 'cell_time'],
  ['Roll amount', 'cell_amount'],
  ['Paid rolls', 'cell_rolls'],
  ['Total spent', 'cell_spent'],
  ['Multipliers', 'cell_multipliers'],
  ['Result', 'cell_result'],
  ['Total win', 'cell_win'],
  ['Net', 'cell_net'],
];

// Paused runs sit with the live ones, not the finished ones.
const RunHistoryPanel: React.FC<RunHistoryPanelProps> = ({
  runs,
  symbol,
  tab,
  onTabChange,
  onView,
  canLoadMore,
  onLoadMore,
  primarySkin,
  secondarySkin,
  periodLabel,
  resultLabel,
  onOpenFilters,
}) => {
  const { t } = useTranslation();
  const [list, setList] = React.useState<HTMLDivElement | null>(null);
  const asTable = useWideEnough(list, TABLE_MIN_WIDTH);

  const skin = (selected: boolean) =>
    ({
      '--button-skin': `url(${selected ? primarySkin : secondarySkin})`,
    }) as React.CSSProperties;

  return (
    <div className={styles.panel}>
      <div className={styles.tabs}>
        {(
          [
            ['completed', t('Completed')],
            ['live', t('In progress')],
          ] as [RunTab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={cn(styles.tab, {
              [styles.tab_on]: tab === key,
              [styles.tab_off]: tab !== key,
            })}
            style={skin(tab === key)}
            onClick={() => onTabChange(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={styles.filters}>
        <button
          type="button"
          className={cn(styles.tab, styles.tab_off)}
          style={skin(false)}
          onClick={onOpenFilters}
        >
          {periodLabel}
        </button>
        <button
          type="button"
          className={cn(styles.tab, styles.tab_off)}
          style={skin(false)}
          onClick={onOpenFilters}
        >
          {resultLabel}
        </button>
      </div>

      <div className={styles.list} ref={setList}>
        {asTable && runs.length > 0 && (
          <div className={styles.columns}>
            {COLUMNS.map(([label, cls]) => (
              <span key={label} className={cn(styles.column, styles[cls])}>
                {t(label)}
              </span>
            ))}
          </div>
        )}

        {runs.map((run) =>
          asTable ? (
            <RunRow
              key={run.journeyId}
              run={run}
              symbol={symbol}
              onView={onView}
            />
          ) : (
            <RunCard
              key={run.journeyId}
              run={run}
              symbol={symbol}
              onView={onView}
            />
          )
        )}
      </div>

      <p className={styles.notice}>
        {t(
          'Paused rounds appear under In progress. A loss may still include previously paid rewards.'
        )}
      </p>

      {canLoadMore && (
        <button
          type="button"
          className={cn(styles.tab, styles.tab_off)}
          style={skin(false)}
          onClick={onLoadMore}
        >
          {t('LOAD MORE')}
        </button>
      )}

      <ActiveRoundNote />
    </div>
  );
};

export default RunHistoryPanel;
